import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Menu,
  X,
  ChevronDown,
  Phone,
  Mail,
  Zap
} from 'lucide-react'
import { useLogo } from '@hooks/useLogo'
import env from '@utils/env'

const courses = [
  { name: 'Linux & Networking Essentials', href: '/courses/linux-and-networking-essentials' },
  { name: 'Cloud Essentials', href: '/courses/cloud-essentials' },
  { name: 'DevOps Essentials', href: '/courses/devops-essentials' },
  { name: 'AWS Professional Architect', href: '/courses/aws-professional-architect' },
  { name: 'GCP Professional Architect', href: '/courses/gcp-professional-architect' },
  { name: 'Azure Architect', href: '/courses/azure-architect' },
  { name: 'Kubernetes Architect', href: '/courses/kubernetes-architect' },
]

const navLinks = [
  { name: 'Home', href: '/' },
  { name: 'DevOps Roadmap', href: '/devops-roadmap' },
  { name: 'Batches', href: '/batches' },
  { name: 'Testimonials', href: '/testimonials' },
  { name: 'Contact', href: '/contact' },
]

// The Courses dropdown sits between Home and the remaining links, on both
// desktop and mobile — splitting the list here keeps that order explicit
// instead of depending on where the dropdown happens to appear in the markup.
const [homeLink, ...secondaryNavLinks] = navLinks

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [desktopCoursesOpen, setDesktopCoursesOpen] = useState(false)
  const [mobileCoursesOpen, setMobileCoursesOpen] = useState(false)
  const { logo: logoSrc } = useLogo()
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsOpen(false)
    setMobileCoursesOpen(false)
  }, [location.pathname])

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [isOpen])

  const isActive = (href) => href === '/' ? location.pathname === '/' : location.pathname.startsWith(href)
  const isCoursesActive = location.pathname.startsWith('/courses')

  const renderDesktopLink = (link) => (
    <Link
      key={link.name}
      to={link.href}
      className={`relative px-3.5 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
        isActive(link.href)
          ? 'text-primary-700 bg-primary-50 dark:text-primary-400 dark:bg-primary-900/40'
          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800'
      }`}
    >
      {link.name}
      {isActive(link.href) && (
        <span className="absolute bottom-1 left-3.5 right-3.5 h-0.5 bg-primary-500 rounded-full" />
      )}
    </Link>
  )

  const renderMobileLink = (link) => (
    <Link
      key={link.name}
      to={link.href}
      className={`flex items-center px-3 py-3 rounded-xl text-sm font-medium transition-colors ${
        isActive(link.href)
          ? 'text-primary-700 bg-primary-50 dark:text-primary-400 dark:bg-primary-900/40'
          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
      }`}
    >
      {link.name}
    </Link>
  )

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white dark:bg-gray-900 shadow-medium border-b border-gray-200 dark:border-gray-800'
          : 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-100 dark:border-gray-800'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">

            {/* Logo */}
            <Link to="/" className="flex items-center shrink-0 group" aria-label="Deoriginlabs - DevOps & Cloud Training Institute">
              <div className="h-24 overflow-hidden flex items-center">
                <img
                  src={logoSrc}
                  alt="Deoriginlabs - DevOps & Cloud Training Institute"
                  width={160}
                  height={48}
                  className="h-48 sm:h-56 md:h-56 lg:h-56 xl:h-56 w-auto object-contain dark:opacity-90 group-hover:scale-105 transition-transform duration-200"
                />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-0.5">
              {renderDesktopLink(homeLink)}

              {/* Courses Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setDesktopCoursesOpen(true)}
                onMouseLeave={() => setDesktopCoursesOpen(false)}
              >
                <button className={`flex items-center gap-1 px-3.5 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  isCoursesActive
                    ? 'text-primary-700 bg-primary-50 dark:text-primary-400 dark:bg-primary-900/40'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}>
                  Courses
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${desktopCoursesOpen ? 'rotate-180' : ''}`} />
                  {isCoursesActive && (
                    <span className="absolute bottom-1 left-3.5 right-3.5 h-0.5 bg-primary-500 rounded-full" />
                  )}
                </button>

                <AnimatePresence>
                  {desktopCoursesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 6, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 6, scale: 0.97 }}
                      transition={{ duration: 0.15, ease: 'easeOut' }}
                      className="absolute top-full left-0 mt-1.5 w-56 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-large py-1.5 overflow-hidden"
                    >
                      {courses.map((course) => (
                        <Link
                          key={course.name}
                          to={course.href}
                          className="block px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900/40 hover:text-primary-700 dark:hover:text-primary-400 transition-colors"
                        >
                          {course.name}
                        </Link>
                      ))}
                      <div className="border-t border-gray-100 mt-1 pt-1">
                        <Link
                          to="/courses"
                          className="block px-4 py-2.5 text-sm font-semibold text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/40 transition-colors"
                        >
                          View All Courses →
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {secondaryNavLinks.map(renderDesktopLink)}
            </div>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href={env.phoneHref}
                className="hidden xl:flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              >
                <Phone className="w-3.5 h-3.5 text-primary-500" />
                {env.phoneDisplay}
              </a>
              <Link
                to="/contact"
                className="flex items-center gap-1.5 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-md shadow-primary-600/25"
              >
                <Zap className="w-3.5 h-3.5" />
                Enroll Now
              </Link>
            </div>

            {/* Mobile controls */}
            <div className="lg:hidden flex items-center gap-1">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle menu"
            >
              <AnimatePresence mode="wait" initial={false}>
                {isOpen ? (
                  <motion.span
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <X className="w-5 h-5" />
                  </motion.span>
                ) : (
                  <motion.span
                    key="open"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <Menu className="w-5 h-5" />
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              className="lg:hidden border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden"
            >
              <div className="max-h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain">
                <div className="px-4 pt-3 pb-2 space-y-0.5">
                  {renderMobileLink(homeLink)}

                  {/* Mobile Courses Collapsible */}
                  <div>
                    <button
                      onClick={() => setMobileCoursesOpen(!mobileCoursesOpen)}
                      className={`flex items-center justify-between w-full px-3 py-3 rounded-xl text-sm font-medium transition-colors ${
                        isCoursesActive
                          ? 'text-primary-700 bg-primary-50 dark:text-primary-400 dark:bg-primary-900/40'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      <span>Courses</span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${mobileCoursesOpen ? 'rotate-180' : ''}`} />
                    </button>

                    <AnimatePresence>
                      {mobileCoursesOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="ml-3 mt-0.5 mb-1 pl-3 border-l-2 border-primary-100 space-y-0.5">
                            {courses.map((course) => (
                              <Link
                                key={course.name}
                                to={course.href}
                                className="block px-3 py-2.5 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:text-primary-700 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/40 transition-colors"
                              >
                                {course.name}
                              </Link>
                            ))}
                            <Link
                              to="/courses"
                              className="block px-3 py-2.5 rounded-lg text-sm font-semibold text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/40 transition-colors"
                            >
                              View All Courses →
                            </Link>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {secondaryNavLinks.map(renderMobileLink)}
                </div>

                {/* Mobile Menu Footer */}
                <div className="px-4 pb-5 pt-3 border-t border-gray-100 dark:border-gray-800 space-y-2.5">
                  <a
                    href={env.phoneHref}
                    className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <span className="w-8 h-8 bg-primary-50 dark:bg-primary-900/40 rounded-lg flex items-center justify-center shrink-0">
                      <Phone className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    </span>
                    {env.phoneDisplay}
                  </a>
                  <a
                    href={`mailto:${env.supportEmail}`}
                    className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <span className="w-8 h-8 bg-primary-50 dark:bg-primary-900/40 rounded-lg flex items-center justify-center shrink-0">
                      <Mail className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    </span>
                    {env.supportEmail}
                  </a>
                  <Link
                    to="/contact"
                    className="flex items-center justify-center gap-2 w-full py-3.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-md shadow-primary-600/20"
                  >
                    <Zap className="w-4 h-4" />
                    Enroll Now
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <div className="h-20" />
    </>
  )
}

export default Navbar
